<template>
  <div class="checkout-step-four">
    <!-- form part 1 -->
    <transition name="slide-fade">
      <form
        @submit.prevent="next('form-1')"
        data-vv-scope="form-1"
        v-if="!steps[4].formPart1Passed"
        novalidate
      >
        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart1.title') }}
                </h3>
                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart1.copy') }}
                </p>
              </div>
              <div class="col-12 col-md-3">
                <small class="checkout-step__copy checkout-step__copy--small">
                  {{ $t('generic.labels.required') }}
                </small>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{ error: errors.has('form-1.billing_name') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_name"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.name') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_name"
                  name="billing_name"
                  :value="billing.name"
                  @input="setBilling({ k: 'name', v: $event.target.value })"
                  @change="setFirstTraveller('first_name', $event.target.value)"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing given-name"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has('form-1.billing_name')"
                >
                  {{ $t('generic.form.errors.name') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{ error: errors.has('form-1.billing_lastname') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_lastname"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.surname') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_lastname"
                  name="billing_lastname"
                  :value="billing.lastname"
                  @input="setBilling({ k: 'lastname', v: $event.target.value })"
                  @change="setFirstTraveller('last_name', $event.target.value)"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing family-name"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has('form-1.billing_lastname')"
                >
                  {{ $t('generic.form.errors.lastname') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{ error: errors.has('form-1.billing_mail') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_mail"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.mail') }}
                </label>
                <input
                  class="form-group__input"
                  type="email"
                  data-vv-validate-on="blur"
                  id="billing_mail"
                  name="billing_mail"
                  :value="billing.mail"
                  @input="setBilling({ k: 'mail', v: $event.target.value })"
                  ref="mail"
                  v-validate="{ required: true, email: true }"
                  autocomplete="billing email"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has('form-1.billing_mail')"
                >
                  {{ $t('generic.form.errors.mail') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{
                  error:
                    fields['$form-1'] &&
                    fields['$form-1'].billing_mail_repeat.touched &&
                    errors.has('form-1.billing_mail_repeat'),
                }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_mail_repeat"
                >
                  {{
                    $t('pages.checkoutStepFour.formPart1.labels.mail_repeat')
                  }}
                </label>
                <input
                  class="form-group__input"
                  type="email"
                  id="billing_mail_repeat"
                  name="billing_mail_repeat"
                  :value="billing.mail_repeat"
                  @input="
                    setBilling({ k: 'mail_repeat', v: $event.target.value })
                  "
                  v-validate="'required|confirmed:mail'"
                  autocomplete="billing email"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="
                    fields['$form-1'] &&
                      fields['$form-1'].billing_mail_repeat.touched &&
                      errors.has('form-1.billing_mail_repeat')
                  "
                >
                  {{ $t('generic.form.errors.mail_repeat') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group">
                <label class="form-group__label" for="billing_phone">
                  {{ $t('pages.checkoutStepFour.formPart1.labels.phone') }}
                </label>
                <input
                  class="form-group__input"
                  type="tel"
                  id="billing_phone"
                  name="billing_phone"
                  :value="billing.phone"
                  maxlength="128"
                  @input="setBilling({ k: 'phone', v: $event.target.value })"
                  autocomplete="billing tel-national"
                  data-1p-ignore
                />
              </div>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart1.traveller.title') }}
                </h3>
                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart1.traveller.copy') }}
                </p>
              </div>
            </div>
          </div>
          <div
            class="form-row"
            v-for="(traveller, index) in travellers"
            :key="index"
            v-if="traveller"
          >
            <div class="col-12 col-sm-4">
              <div
                class="form-group"
                :class="{
                  error: errors.has(`form-1.traveller_${index}_first_name`),
                }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`form-1.traveller_${index}_first_name`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.name') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  :id="`traveller_${index}_first_name`"
                  :name="`traveller_${index}_first_name`"
                  :value="traveller.first_name"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  @input="
                    setTraveller({
                      k: 'first_name',
                      i: index,
                      v: $event.target.value,
                    })
                  "
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_first_name`)"
                >
                  {{ $t('generic.form.errors.name') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4">
              <div
                class="form-group"
                :class="{
                  error: errors.has(`form-1.traveller_${index}_last_name`),
                }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`traveller_${index}_last_name`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.surname') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  :id="`traveller_${index}_last_name`"
                  :name="`traveller_${index}_last_name`"
                  :value="traveller.last_name"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  @input="
                    setTraveller({
                      k: 'last_name',
                      i: index,
                      v: $event.target.value,
                    })
                  "
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_last_name`)"
                >
                  {{ $t('generic.form.errors.lastname') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4">
              <div
                class="form-group"
                :class="{
                  error: errors.has(`form-1.traveller_${index}_birthdate`),
                }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  :for="`traveller_${index}_birthdate`"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.labels.birthDate') }}
                </label>
                <ci-datepicker
                  :value="traveller.birthdate"
                  :config="pickerConfig"
                  :open="getDatepickerOpen(index)"
                  :locale="langSlug"
                  @toggle-open="setDatepickerOpen(index, $event)"
                  @input="setTraveller({ k: 'birthdate', i: index, v: $event })"
                  :inputId="`traveller_${index}_birthdate`"
                  :name="`traveller_${index}_birthdate`"
                  v-validate="'required'"
                >
                </ci-datepicker>
                <small
                  class="form-group__small"
                  v-if="errors.has(`form-1.traveller_${index}_birthdate`)"
                >
                  {{ $t('generic.form.errors.birthdate') }}
                </small>
              </div>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart1.car.title') }}
                </h3>
                <p class="checkout-step__copy" v-if="showCarDetails">
                  {{
                    $t('pages.checkoutStepFour.formPart1.car.copyMeasurements')
                  }}
                </p>
                <p class="checkout-step__copy" v-if="!showCarDetails">
                  {{ $t('pages.checkoutStepFour.formPart1.car.copy') }}
                </p>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12 col-sm-4">
              <div class="form-group">
                <label class="form-group__label" for="car_license_plate">
                  {{ $t('pages.checkoutStepFour.formPart1.car.license_plate') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="car_license_plate"
                  name="car_license_plate"
                  :value="car.license_plate"
                  maxlength="32"
                  v-validate="{ required: false, max: 32 }"
                  @input="
                    setCar({ k: 'license_plate', v: $event.target.value })
                  "
                  data-1p-ignore
                />
              </div>
            </div>
            <div class="col-12 col-sm-4" v-if="showCarDetails">
              <div
                class="form-group"
                :class="{ error: errors.has('form-1.car_length') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="car_length"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.car.kfzLength') }}
                </label>
                <div class="suffix-wrapper">
                  <input
                    class="form-group__input"
                    type="number"
                    id="car_length"
                    name="car_length"
                    min="1"
                    max="50"
                    :value="car.length"
                    v-validate="{ required: true, max_value: 50, min_value: 1 }"
                    @input="setCar({ k: 'length', v: $event.target.value })"
                    data-1p-ignore
                  />
                </div>
                <small
                  class="form-group__small"
                  v-if="errors.has('form-1.car_length')"
                >
                  <span
                    v-if="errors.firstByRule('form-1.car_length', 'min_value')"
                    >{{
                      $t('generic.form.errors.car.lengthMin', { min: 1 })
                    }}</span
                  >
                  <span
                    v-else-if="
                      errors.firstByRule('form-1.car_length', 'max_value')
                    "
                    >{{
                      $t('generic.form.errors.car.lengthMax', { max: 50 })
                    }}</span
                  >
                  <span v-else>{{ $t('generic.form.errors.car.length') }}</span>
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4" v-if="showCarDetails">
              <div
                class="form-group"
                :class="{ error: errors.has('form-1.car_width') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="car_width"
                >
                  {{ $t('pages.checkoutStepFour.formPart1.car.kfzWidth') }}
                </label>
                <div class="suffix-wrapper">
                  <input
                    class="form-group__input"
                    type="number"
                    id="car_width"
                    name="car_width"
                    min="1"
                    max="10"
                    :value="car.width"
                    v-validate="{ required: true, min_value: 1, max_value: 10 }"
                    @input="setCar({ k: 'width', v: $event.target.value })"
                    data-1p-ignore
                  />
                </div>
                <small
                  class="form-group__small"
                  v-if="errors.has('form-1.car_width')"
                >
                  <span
                    v-if="errors.firstByRule('form-1.car_width', 'min_value')"
                    >{{
                      $t('generic.form.errors.car.widthMin', { min: 1 })
                    }}</span
                  >
                  <span
                    v-else-if="
                      errors.firstByRule('form-1.car_width', 'max_value')
                    "
                    >{{
                      $t('generic.form.errors.car.widthMax', { max: 10 })
                    }}</span
                  >
                  <span v-else>{{ $t('generic.form.errors.car.width') }}</span>
                </small>
              </div>
            </div>
          </div>
        </section>

        <CiGenericNavigation
          @clickSecondary="back('form-1')"
          @clickPrimary="next('form-1')"
        >
          <template slot="secondary">
            <iconArrowBlack class="icon icon--animated"></iconArrowBlack>
            <span>{{ $t('generic.buttons.stepBack') }}</span>
          </template>
          <template slot="primary">
            <span>{{ $t('generic.buttons.next') }}</span>
            <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
          </template>
        </CiGenericNavigation>
      </form>
    </transition>

    <!-- form part 2 -->
    <transition name="slide-fade">
      <form data-vv-scope="form-2" v-if="steps[4].formPart1Passed" novalidate>
        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart2.title') }}
                </h3>

                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart2.copy') }}
                </p>
              </div>
              <div class="col-12 col-md-3">
                <small class="checkout-step__copy checkout-step__copy--small">
                  {{ $t('generic.labels.required') }}
                </small>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{ error: errors.has('form-2.billing_street') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_street"
                >
                  {{ $t('pages.checkoutStepFour.formPart2.labels.street') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_street"
                  name="billing_street"
                  :value="billing.street"
                  @input="setBilling({ k: 'street', v: $event.target.value })"
                  maxlength="254"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing address-line1"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has('form-2.billing_street')"
                >
                  {{ $t('generic.form.errors.street') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div class="form-group">
                <label
                  class="form-group__label"
                  for="billing_street_additional"
                >
                  {{
                    $t(
                      'pages.checkoutStepFour.formPart2.labels.streetAdditional'
                    )
                  }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_street_additional"
                  name="billing_street_additional"
                  maxlength="254"
                  :value="billing.street_additional"
                  @input="
                    setBilling({
                      k: 'street_additional',
                      v: $event.target.value,
                    })
                  "
                  autocomplete="billing address-line2"
                  data-1p-ignore
                />
              </div>
            </div>
            <div class="col-12 col-sm-2">
              <div
                class="form-group"
                :class="{ error: errors.has('form-2.billing_zip') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_zip"
                >
                  {{ $t('pages.checkoutStepFour.formPart2.labels.zip') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_zip"
                  name="billing_zip"
                  :value="billing.zip"
                  @input="setBilling({ k: 'zip', v: $event.target.value })"
                  maxlength="32"
                  v-validate="{ required: true, max: 32 }"
                  autocomplete="billing postal-code"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has('form-2.billing_zip')"
                >
                  {{ $t('generic.form.errors.zip') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-4">
              <div
                class="form-group"
                :class="{ error: errors.has('form-2.billing_city') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_city"
                >
                  {{ $t('pages.checkoutStepFour.formPart2.labels.city') }}
                </label>
                <input
                  class="form-group__input"
                  type="text"
                  id="billing_city"
                  name="billing_city"
                  :value="billing.city"
                  maxlength="254"
                  @input="setBilling({ k: 'city', v: $event.target.value })"
                  v-validate="{ required: true, max: 254 }"
                  autocomplete="billing address-level2"
                  data-1p-ignore
                />
                <small
                  class="form-group__small"
                  v-if="errors.has('form-2.billing_city')"
                >
                  {{ $t('generic.form.errors.city') }}
                </small>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <div
                class="form-group"
                :class="{ error: errors.has('form-2.billing_country') }"
              >
                <label
                  class="form-group__label form-group__label--required"
                  for="billing_country"
                >
                  {{ $t('pages.checkoutStepFour.formPart2.labels.country') }}
                </label>
                <multiselect
                  id="billing_country"
                  :value="billing.country"
                  :options="countries"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :placeholder="$t('generic.labels.choose')"
                  name="billing_country"
                  @input="setCountry"
                  track-by="label"
                  label="label"
                  v-validate="'required'"
                  autocomplete="billing country"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{
                    option.label
                  }}</template>
                  <template slot="noResult">{{
                    $t('generic.labels.noResult')
                  }}</template>
                </multiselect>
                <small
                  class="form-group__small"
                  v-if="errors.has('form-2.billing_country')"
                >
                  {{ $t('generic.form.errors.country') }}
                </small>
              </div>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="checkout-step__header">
            <div class="row align-items-end">
              <div class="col-12 col-md-9">
                <h3 class="checkout-step__headline">
                  {{ $t('pages.checkoutStepFour.formPart2.campsite.title') }}
                </h3>
                <p class="checkout-step__copy">
                  {{ $t('pages.checkoutStepFour.formPart2.campsite.copy') }}
                </p>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-12">
              <div class="form-group">
                <label class="form-group__label" for="billing_message">
                  {{ $t('generic.labels.message') }}
                </label>
                <textarea
                  class="form-group__input"
                  id="billing_message"
                  rows="8"
                  name="billing_message"
                  :value="billing.message"
                  @input="setBilling({ k: 'message', v: $event.target.value })"
                >
                </textarea>
              </div>
            </div>
          </div>
        </section>

        <CiGenericNavigation
          @clickSecondary="back('form-2')"
          @clickPrimary="next('form-2')"
        >
          <template slot="secondary">
            <iconArrowBlack class="icon icon--animated"></iconArrowBlack>
            <span>{{ $t('generic.buttons.stepBack') }}</span>
          </template>
          <template slot="primary">
            <span>{{ $t('generic.buttons.nextStep') }}</span>
            <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
          </template>
        </CiGenericNavigation>
      </form>
    </transition>
  </div>
</template>

<script>
import CiDatepicker from 'ci-datepicker'

import { mapGetters, mapActions } from 'vuex'
import { CiSelectionBox, CiGenericNavigation } from '@/components/index'
import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'

export default {
  name: 'CheckoutStepFour',
  components: {
    CiDatepicker,
    CiGenericNavigation,
    CiSelectionBox,
    iconArrowBlack,
    iconArrowWhite,
  },
  data() {
    return {
      pickerConfig: {
        range: false,
        inline: false,
        closeAfterSelect: true,
        minDate: new Date(1900, 0, 1),
        maxDate: new Date(),
        dateFormat: this.$t('generic.date.format'),
        i18n: this.datepickerI18n(),
        birthdateMode: true,
        breakpoint: 10000,
        align: 'right',
        pickerWidthPixelOffset: -1,
      },
      datepickersOpen: [],
    }
  },
  created() {
    this.trackEcommerceEvents(4)
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      steps: 'steps',
      langSlug: 'langSlug',
    }),

    ...mapGetters('booking', {
      car: 'car',
      billing: 'billing',
      selectedCampsite: 'selectedCampsite',
      travellers: 'travellers',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
    }),

    showCarDetails() {
      if (!this.selectedCampsite) {
        return true
      }
      return (
        this.selectedCampsite.campsite_type === 'camper' ||
        this.selectedCampsite.campsite_type === 'caravan'
      )
    },

    formValid() {
      return this.errors.items.length <= 0
    },
  },
  methods: {
    ...mapActions('application', {
      setApplicationStepStatus: 'setStepStatus',
      toggleBookingDetails: 'toggleBookingDetails',
      trackEcommerceEvents: 'trackEcommerceEvents',
    }),

    ...mapActions('booking', {
      setBilling: 'setBilling',
      setCar: 'setCar',
      setTraveller: 'setTraveller',
    }),

    setFirstTraveller(key, value) {
      // sets data from billing to first traveller if no value is set
      /* istanbul ignore else */
      if (this.fields['$form-1'][`traveller_0_${key}`].untouched) {
        this.setTraveller({ k: key, i: 0, v: value })
        this.$nextTick(() => {
          this.$validator.validate(`form-1.traveller_0_${key}`)
        })
      }
    },

    setCountry(value) {
      this.setBilling({ k: 'country', v: value })
    },

    getDatepickerOpen(idx) {
      return !!this.datepickersOpen[idx]
    },

    setDatepickerOpen(idx, open) {
      this.$set(this.datepickersOpen, idx, open)
    },

    back(formScope) {
      if (formScope === 'form-1') {
        this.$router.push({
          name: 'Step',
          params: { ...this.$route.params, step: this.activeStep - 1 },
        })
      } else {
        this.setStepStatus({ number: 4, key: 'formPart1Passed', value: false })
        window.scrollTo(0, 0)
        this.trackEcommerceEvents(4)
      }
    },

    next(formScope) {
      // in case of form-1 and billing_mail_repeat has not been touched, set it to touched,
      // otherwise no error message will be shown
      /* istanbul ignore else */
      if (
        formScope === 'form-1' &&
        !this.fields['$form-1'].billing_mail_repeat.touched
      ) {
        this.$validator.flag('form-1.billing_mail_repeat', { touched: true })
      }

      return this.$validator.validateAll(formScope).then(result => {
        /* istanbul ignore else */
        if (!result && this.$el.querySelector('.form-group.error')) {
          this.$el.querySelector('.form-group.error').scrollIntoView()
        }

        if (!result) {
          let errors = []
          Object.values(this.$validator.errors.items).forEach(item => {
            errors.push(item.field)
          })
        }

        /* istanbul ignore else */
        if (result && formScope === 'form-1') {
          this.setStepStatus({
            number: 4,
            key: 'formPart1Passed',
            value: result,
          })
          this.trackEcommerceEvents(4)
          window.scrollTo(0, 0)
        }

        /* istanbul ignore else */
        if (result && formScope === 'form-2') {
          this.setStepStatus({ number: 4, key: 'fulfilled', value: true })
          this.trackEcommerceEvents(4)
          this.toggleBookingDetails()
        }
      })
    },

    datepickerI18n() {
      return {
        nights: [
          this.$tc('generic.date.nights', 1),
          this.$tc('generic.date.nights', 2),
        ],
        until: this.$t('generic.until'),
        today: this.$t('generic.today'),
        inputFormat: this.$t('generic.date.birthdateInputFormat'),
        placeholder: this.$t('generic.date.birthdatePlaceholder'),
        inputPlaceholder: this.$t('generic.date.birthdateInputPlaceholder'),
      }
    },

    onFormValidChange(newValue) {
      /* istanbul ignore else */
      if (!newValue) {
        this.setStepStatus({ number: 4, key: 'fulfilled', value: false })
      }
    },

    setStepStatus(params) {
      this.setApplicationStepStatus(params)
    },
  },
  watch: {
    formValid: {
      handler: 'onFormValidChange',
    },
    'billing.name'(val){
        if (process.env.VUE_APP_ENVNAME !== 'prod' && (val && val.toLowerCase() === 'mctest')) {
          this.setBilling({ k: 'name', v: 'John' })
          this.setBilling({ k: 'lastname', v: 'Doe' })
          this.setBilling({ k: 'mail', v: 'tools+checkouttest@acsicamping.info' })
          this.setBilling({ k: 'mail_repeat', v: 'tools+checkouttest@acsicamping.info' })
          this.setBilling({ k: 'phone', v: '+49123456789' })
          Object.keys(this.travellers).forEach((index) => {
            this.setTraveller({ k: 'first_name', i: index, v: 'John' })
            this.setTraveller({ k: 'last_name', i: index, v: 'Doe' })
            this.setTraveller({ k: 'birthdate', i: index, v: '1990-03-15' })
          })
          this.setCar({ k: 'license_plate', v: 'B 1234'})
          if(this.showCarDetails) {
            this.setCar({ k: 'length', v: '26' })
            this.setCar({ k: 'width', v: '8.2' })
          }
          this.setBilling({ k: 'street', v: 'Hauptstraße 25' });
          this.setBilling({ k: 'street_additional', v: 'Apartment 3B' });
          this.setBilling({ k: 'zip', v: '10115' });
          this.setBilling({ k: 'city', v: 'Berlin' });
          this.setBilling({ k: 'country', v: { "code": "de", "label": "Germany" } });
          this.setBilling({ k: 'message', v: 'Hallo' });
        }
      },
  },
}
</script>
<style lang="scss" scoped>
.checkout-step-four__header {
  margin: 0 0 30px;
}

.checkout-step-four__headline {
  @include font-format(1.375rem, 1.625em);
}

.checkout-step-four__copy {
  margin: 0;

  &.checkout-step-four__copy--small {
    @include font-format(0.625em, 0.875em);
  }
}

.checkout-step-four__navigation {
  display: flex;
}

.suffix-wrapper {
  position: relative;

  input[type='number'] {
    -moz-appearance: textfield;
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  &::after {
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    right: 15px;
    content: 'm';
    opacity: 0.6;
  }
}
</style>
